import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Sidebar from "../DesignSystem/Sidebar";
import Breadcrumb from "../DesignSystem/Breadcrumb/Breadcrumb";
import { useUserEmail } from "../SignIn/UserEmailContext";
import { useUserPermissions } from "../SignIn/UserPermissionsContext";
import { secretSantaRequest, subscribeSecretSantaParticipants } from "./secretSantaApi";
import "../../css/Marketing/FinancialPromotions/FinancialPromotions.css";
import "../../css/Marketing/FinancialPromotions/FinancialPromotionsSubmission.css";
import "../../css/DesignSystem/Buttons/PrimaryButton.css";
import "../../css/Marketing/WebsiteCharges.css";

export default function SecretSantaPage() {
  const { userEmail } = useUserEmail() || {};
  const { userPermissions } = useUserPermissions();
  const canRevealMatches = Array.isArray(userPermissions) && userPermissions.includes("Administrator");
  const [session, setSession] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [saveNotice, setSaveNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [draws, setDraws] = useState([]);
  const [drawsLoaded, setDrawsLoaded] = useState(false);
  const [title, setTitle] = useState("Rosemount Secret Santa 2026");
  const [savedParticipants, setSavedParticipants] = useState([]);
  const [participantsReady, setParticipantsReady] = useState(false);
  const people = savedParticipants
    .filter((person) => person.enabled !== false)
    .map((person) => `${person.name}, ${person.email}`)
    .join("\n");
  const [message] = useState("");
  // const [testMode, setTestMode] = useState(true);
  const testMode = false;
  const [confirmed, setConfirmed] = useState("");
  const [revealed, setRevealed] = useState("");
  const lockVersion = useRef(0);


  const lock = useCallback(() => {
    lockVersion.current += 1;
    setSession("");
    setDraws([]);
    setDrawsLoaded(false);
    setSavedParticipants([]);
    setParticipantsReady(false);
    setRevealed("");
    setConfirmed("");
    setPassword("");
    setShowPassword(false);
    setSaveNotice("");
  }, []);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") lock();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pagehide", lock);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", lock);
    };
  }, [lock]);

  useEffect(() => {
    if (!session) return undefined;
    return subscribeSecretSantaParticipants((participants) => {
      setSavedParticipants(participants);
      setParticipantsReady(true);
      setError("");
    }, (failure) => {
      setSavedParticipants([]);
      setParticipantsReady(false);
      setError(failure.message);
      if ([401, 403, 423].includes(failure.status)) lock();
    });
  }, [session, lock]);

  async function run(action) {
    setBusy(true);
    setError("");
    setSaveNotice("");
    try {
      await action();
    } catch (e) {
      setError(e.message);
      if ([401, 403, 423].includes(e.status)) lock();
    } finally {
      setBusy(false);
    }
  }
  const refresh = async (key = session) => {
    const version = lockVersion.current;
    const result = await secretSantaRequest("/draws", key);
    if (version !== lockVersion.current) return;
    setDraws(result.draws);
    setDrawsLoaded(true);
  };
  const unlock = (event) => {
    event.preventDefault();
    const version = lockVersion.current;
    run(async () => {
      const result = await secretSantaRequest("/unlock", "", { password: password.trim() });
      if (version !== lockVersion.current || document.visibilityState === "hidden") return;
      setPassword("");
      setShowPassword(false);
      setSession(result.session);
      await refresh(result.session);
    });
  };
  const create = (event) => {
    event.preventDefault();
    run(async () => {
      const participants = people
        .split(/\r?\n/)
        .filter((line) => line.trim())
        .map((line) => {
          const split = line.lastIndexOf(",");
          if (split < 1)
            throw new Error("Use Name, email@example.com on each line.");
          return {
            name: line.slice(0, split).trim(),
            email: line.slice(split + 1).trim(),
          };
        });
      const result = await secretSantaRequest("/draws", session, {
        title,
        participants,
        testMode,
        actorEmail: userEmail || null,
        budget: "£10",
        exchangeDate: "11 December 2026",
        message,
      });
      setDraws((current) => [result.draw, ...current]);
      setSaveNotice(`Draw saved successfully for ${result.draw.matches.length} participants. Reference: ${result.draw.id}. No emails have been sent.`);
      console.info("[Secret Santa] Draw saved", {
        id: result.draw.id,
        participantCount: result.draw.matches.length,
        testMode: result.draw.testMode === true,
        savedAt: result.draw.createdAt,
      });
    });
  };

  return (
    <div className="main-content">
      <Helmet>
        <title>Secret Santa | Rosemount</title>
      </Helmet>
      <Sidebar />
      <main className="Main-Container fp-submission-container christmas-background">
        <Breadcrumb
          items={[
            { label: "Administrator", to: "/Department/Administrator" },
            { label: "Secret Santa" },
          ]}
        />
        <header className="fp-form-container christmas-green-surface">
          <p className="FP_Body">🎄 ROSEMOUNT CHRISTMAS 🎁</p>
          <h3 className="FP_H3 white-text">Secret Santa</h3>
          {/* <p className="FP_Body">A little mystery. A little Christmas magic.</p> */}
          <strong className="FP_Body_Semibold">
            £10 gift budget · 11 December 2026
          </strong>
        </header>
        {error && (
          <p
            className="fp-form-container surface-error FP_Content"
            role="alert"
          >
            {error}
          </p>
        )}
        {session && saveNotice && <p className="fp-form-container FP_Body" role="status">{saveNotice}</p>}
        {!session ? (
          <section className="fp-form-container christmas-red-accent">
            {/* <h2 className="FP_H3 christmas-green-text">Unlock the Christmas Secret Santa</h2> */}
            {/* <p className="FP_Body">Enter password</p> */}
            <form className="fp-form-container" onSubmit={unlock}>
              <label className="FP_Content_Semibold" htmlFor="santa-password">
                Secret Santa password
              </label>
              <div className="password-input-control">
              <input className="fp-form-input FP_Content" id="santa-password"
                type={showPassword ? "text" : "password"}
                autoComplete="off"
                maxLength={256}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button type="button" className="password-visibility-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword} aria-controls="santa-password"
                onClick={() => setShowPassword((visible) => !visible)}>
                {showPassword ? <VisibilityOff aria-hidden="true" /> : <Visibility aria-hidden="true" />}
              </button>
              </div>
              <button
                className="ds-primary-btn christmas-red-button FP_Content_Semibold"
                disabled={busy}
              >
                {busy ? "Unlocking…" : "Unlock Secret Santa"}
              </button>
            </form>
          </section>
        ) : (
          <>
            {/* <div className="fp-form-checkbox-group">
              <button
                className="ds-primary-btn christmas-red-button FP_Content_Semibold"
                disabled={busy}
                onClick={() => run(() => refresh())}
              >
                Refresh draws
              </button>
              <button
                className="ds-primary-btn christmas-red-button FP_Content_Semibold"
                disabled={busy}
                onClick={lock}
              >
                Lock page
              </button>
            </div> */}
            {!drawsLoaded && <p className="fp-form-container FP_Body" role="status">Loading saved draw…</p>}
            {drawsLoaded && draws.length === 0 && <section className="fp-form-container christmas-red-accent">
              <h2 className="FP_H3 christmas-green-text">Create a Christmas draw</h2>

              <form className="fp-form-container" onSubmit={create}>
                {/* <label className="fp-form-checkbox-label FP_Content">
                  <input type="checkbox" checked={testMode} disabled={busy}
                    onChange={(event) => setTestMode(event.target.checked)} />{" "}
                  Test draw - allow two participants and prefix email subjects with [TEST]
                </label> */}
                <label className="FP_Content_Semibold" htmlFor="santa-title">
                  Event name
                </label>
                <input className="fp-form-input FP_Content" id="santa-title"
                  required
                  maxLength={120}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <label className="FP_Content_Semibold" htmlFor="santa-people">
                  Participants - Name, email address
                </label>
                <textarea className="fp-form-input FP_Content" id="santa-people"
                  rows={8}
                  required
                  placeholder={
                    "Alex Example, alex@example.com\nSam Example, sam@example.com\nJo Example, jo@example.com"
                  }
                  value={people}
                  readOnly
                />

                <button
                  className="ds-primary-btn christmas-red-button FP_Content_Semibold"
                  disabled={busy || !participantsReady}
                >
                  {busy ? "Please wait…" : "Create and save draw"}
                </button>
              </form>
            </section>}
            {/* {!draws.length && (
              <section className="fp-form-container christmas-red-accent">
                <p className="FP_Help_Text">
                  No draws yet. Add your participants above to get started.
                </p>
              </section>
            )} */}
            {draws.map((draw) => (
              <section className="fp-form-container christmas-red-accent" key={draw.id}>
                <h2 className="FP_H3 christmas-green-text">{draw.title}</h2>
                <p className="FP_Content">Saved reference: {draw.createdAt ? ` · ${new Date(draw.createdAt).toLocaleString("en-GB")}` : ""}</p>

                {draw.lastSendRequestedAt && <p className="FP_Content">Send initiated by: {draw.lastSendRequestedByEmail || "Not recorded"} · {new Date(draw.lastSendRequestedAt).toLocaleString("en-GB")}</p>}

                <p className="FP_Help_Semibold">
                  {draw.budget} · {draw.exchangeDate} · {draw.matches.length}{" "}
                  participants
                </p>
                {canRevealMatches && <button
                  className="ds-primary-btn christmas-red-button FP_Content_Semibold"
                  disabled={busy}
                  onClick={() =>
                    setRevealed(revealed === draw.id ? "" : draw.id)
                  }
                >
                  {revealed === draw.id
                    ? "Hide matches"
                    : "Reveal matches for admin"}
                </button>}
                <div className="website-dashboard-card website-table-wrap table-rows-scroll-container table-scroll-container">
                  <table className="website-table">
                    <thead>
                      <tr>
                        <th className="FP_Help_Text_Semibold white-text uppercase" scope="col">
                          No.
                        </th>
                        <th className="FP_Help_Text_Semibold white-text uppercase">
                          Participant
                        </th>
                        <th className="FP_Help_Text_Semibold white-text uppercase">
                          Email
                        </th>
                        {canRevealMatches && revealed === draw.id && (
                          <th className="FP_Help_Text_Semibold white-text uppercase">
                            Buying for
                          </th>
                        )}
                        <th className="FP_Help_Text_Semibold white-text uppercase">
                          Email status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...draw.matches].sort((a, b) =>
                        a.giver.name.localeCompare(b.giver.name, "en-GB", { sensitivity: "base" }) ||
                        a.giver.email.localeCompare(b.giver.email)
                      ).map((match, index) => (
                        <tr key={match.giver.email}>
                          <td className="FP_Help_Text black">{index + 1}</td>
                          <td className="FP_Help_Text black">{match.giver.name}</td>
                          <td className="FP_Help_Text black">{match.giver.email}</td>
                          {canRevealMatches && revealed === draw.id && (
                            <td className="FP_Help_Text black">{match.recipient.name}</td>
                          )}
                          <td className="FP_Help_Text black">
                            {
                              {
                                pending: "Not sent",
                                sending: "Sending / check mailbox",
                                accepted: "Sent",
                                uncertain: "Check mailbox before retrying",
                              }[match.status]
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {draw.matches.some((match) =>
                  ["sending", "uncertain"].includes(match.status),
                ) && (
                  <p role="status">
                    Some sends could not be confirmed. Check the sender’s Sent
                    Items before any manual resend. These messages will not be
                    retried automatically.
                  </p>
                )}
                {!draw.legacyReadOnly && draw.matches.some((match) => match.status === "pending") && (
                  <>
                    <label className="fp-form-checkbox-label FP_Content">
                      <input
                        type="checkbox"
                        disabled={busy}
                        checked={confirmed === draw.id}
                        onChange={(e) =>
                          setConfirmed(e.target.checked ? draw.id : "")
                        }
                      />{" "}
                      I have checked the participants and want to email each
                      person their match.
                    </label>
                    <button
                      className="ds-primary-btn christmas-red-button FP_Content_Semibold"
                      disabled={busy || confirmed !== draw.id}
                      onClick={() =>
                        run(async () => {
                          console.info("[Secret Santa] Send requested", { drawId: draw.id, testMode: draw.testMode === true });
                          const result = await secretSantaRequest(
                            `/draws/${draw.id}/send`,
                            session,
                            { actorEmail: userEmail || null },
                          );
                          console.info("[Secret Santa] Send results", {
                            drawId: result.draw.id,
                            statuses: result.draw.matches.map((match, index) => ({ participant: index + 1, status: match.status })),
                          });
                          setDraws((current) =>
                            current.map((item) =>
                              item.id === draw.id ? result.draw : item,
                            ),
                          );
                          setConfirmed("");
                        })
                      }
                    >
                      {busy
                        ? "Please wait…"
                        : "Send Christmas emails"}
                    </button>
                  </>
                )}
              </section>
            ))}
          </>
        )}
      </main>
    </div>
  );
}
